import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Zap, Crown, Loader2, KeyRound, Settings } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PRO_FEATURES } from "@/lib/proFeatures";

function PlanCard({ title, price, per, note, badge, lookupKey, onPick, busy, trialDays, highlight }) {
  return (
    <Card className={`relative bg-[#111723] p-7 flex flex-col ${highlight ? "border-emerald-500/60 shadow-[0_0_40px_-12px_rgba(0,255,102,0.45)]" : "border-[#222F43]"}`} data-testid={`plan-${lookupKey}`}>
      {badge && <Badge className="absolute -top-3 left-6 bg-emerald-500 text-black font-bold">{badge}</Badge>}
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{title}</div>
      <div className="mt-2 flex items-end gap-1">
        <span className="font-mono font-extrabold text-4xl">{price}</span>
        <span className="text-slate-500 text-sm mb-1.5">/{per}</span>
      </div>
      <div className="text-xs text-slate-400 mt-1 min-h-[16px]">{note}</div>
      <Button onClick={() => onPick(lookupKey)} disabled={!!busy} data-testid={`checkout-${lookupKey}`}
        className={`mt-6 rounded-full font-display font-extrabold ${highlight ? "bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17]" : "bg-[#1a2333] hover:bg-[#222F43] text-slate-100 border border-[#334566]"}`}>
        {busy === lookupKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Zap className="h-4 w-4 mr-1.5" /> Start {trialDays}-day free trial</>}
      </Button>
      <p className="text-[10px] text-slate-500 mt-2 text-center font-mono">Card required · cancel anytime before day {trialDays}</p>
    </Card>
  );
}

export default function Pricing() {
  const { user, openAuth } = useAuth();
  const { plan, pro, startCheckout, openPortal, refreshPlan } = usePlan();
  const [busy, setBusy] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const trialDays = plan?.trialDays ?? 3;

  const pick = async (lookupKey) => {
    if (!user) { toast("Sign in first — your plan is tied to your account"); openAuth(); return; }
    setBusy(lookupKey);
    try { await startCheckout(lookupKey); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(null); }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!user) { openAuth(); return; }
    setRedeeming(true);
    try {
      await api.post("/billing/redeem", { code });
      toast.success("Lifetime access unlocked. Welcome aboard.");
      setCode("");
      refreshPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  const sub = plan?.subscription;

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16" data-testid="pricing-page">
      <div className="max-w-2xl mb-10">
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 font-mono text-[10px] tracking-widest mb-4">CATALYSTCADDY PRO</Badge>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.05]">Know what's moving <span className="text-emerald-400">before</span> it moves.</h1>
        <p className="text-slate-400 mt-4 text-base">Unlimited rug scans, the full Catalyst Calendar, AI takes and a trade journal that actually makes you better. One tool, no tab-juggling.</p>
      </div>

      {pro ? (
        <Card className="bg-emerald-500/8 border-emerald-500/40 p-6 mb-10 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="pro-status-card">
          <Crown className="h-8 w-8 text-emerald-400 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg">You're Pro{plan.source === "lifetime" ? " — lifetime" : plan.source === "trial" ? " — free trial" : plan.source === "admin" ? " — admin" : ""}</div>
            <div className="text-sm text-slate-400 font-mono">
              {sub?.trial_end && plan.source === "trial" && `Trial ends ${new Date(sub.trial_end).toLocaleDateString()} · then ${sub.lookup_key === "pro_yearly" ? "$250/yr" : "$29.99/mo"}`}
              {sub && plan.source === "subscription" && `${sub.lookup_key === "pro_yearly" ? "Yearly" : "Monthly"} · ${sub.cancel_at_period_end ? "cancels" : "renews"} ${sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : ""}`}
              {plan.source === "lifetime" && "Thanks for being early. Never expires."}
            </div>
          </div>
          {sub?.stripe_customer_id && (
            <Button variant="outline" onClick={openPortal} className="border-[#334566] bg-transparent" data-testid="manage-billing-btn">
              <Settings className="h-4 w-4 mr-1.5" /> Manage billing
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5 mb-10">
          <PlanCard title="Monthly" price="$29.99" per="mo" note="Flexible. Cancel anytime." lookupKey="pro_monthly" onPick={pick} busy={busy} trialDays={trialDays} />
          <PlanCard title="Yearly" price="$250" per="yr" note="≈ $20.83/mo · save 30% vs monthly" badge="BEST VALUE" lookupKey="pro_yearly" onPick={pick} busy={busy} trialDays={trialDays} highlight />
        </div>
      )}

      <div className="grid md:grid-cols-[1fr_320px] gap-8 items-start">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-4">Everything in Pro</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
            {PRO_FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.title} className="flex gap-3" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="h-8 w-8 rounded-lg bg-emerald-500/12 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-emerald-400" /></div>
                  <div><div className="font-semibold text-sm">{f.title}</div><div className="text-xs text-slate-400 mt-0.5">{f.desc}</div></div>
                </li>
              );
            })}
          </ul>
          <div className="mt-8 text-xs text-slate-500 flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-400" /> Free accounts get {plan?.freeScansPerDay ?? 1} scan a day and can browse the <Link to="/catalysts" className="text-emerald-400 underline-offset-2 hover:underline">Catalyst Calendar</Link>.</div>
        </div>

        {!pro && (
          <Card className="bg-[#111723] border-[#222F43] p-6" data-testid="redeem-card">
            <div className="flex items-center gap-2 mb-1"><KeyRound className="h-4 w-4 text-amber-400" /><div className="font-display font-bold">Have a code?</div></div>
            <p className="text-xs text-slate-400 mb-4">Founding codes unlock lifetime Pro — no card, no renewals.</p>
            <form onSubmit={redeem} className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CADDY-XXXXXX" className="bg-[#0B0F17] border-[#334566] font-mono uppercase" data-testid="redeem-input" />
              <Button type="submit" disabled={redeeming || code.length < 3} className="bg-amber-400 hover:bg-amber-300 text-black font-bold shrink-0" data-testid="redeem-btn">
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
              </Button>
            </form>
            {!user && <p className="text-[10px] text-slate-500 mt-2 font-mono">You'll be asked to sign in first.</p>}
          </Card>
        )}
      </div>
    </div>
  );
}
